import apiClient, { sendKeepalive } from "../api/client";
import { following } from "../constants/actionTypes";
import { isCanceledRequest } from "../helpers/isCanceledRequest";

const SCOPE_URL = "/members/api-v2/user-following/scope";
const LOCATIONS_URL = "/members/api-v2/user-following/locations";
const COMMUNITIES_URL = "/members/api-v2/user-following/communities";
const LANGUAGES_URL = "/members/api-v2/communities";
const PLACES_URL = "/members/api-v2/places";
const SUGGESTED_COMMUNITIES_URL = "/members/api-v2/feed/suggested-communities";

/**
 * Structured failure payload. apiv2 helpers answer errors as `{ ok: false, error }`.
 *
 * @param {string} section errors.<section> key
 * @param {Error} error axios error
 * @param {string} fallback user-facing message when the server sent none
 */
const failurePayload = (section, error, fallback) => ({
  section,
  message: error?.response?.data?.error || fallback,
  status: error?.response?.status || null
});

/**
 * `sendKeepalive` answered in axios's shape, so one success path and `failurePayload` serve both:
 * resolves `{ data }` on 2xx, otherwise rejects with an Error carrying `response: { status, data }`.
 */
const keepaliveRequest = (method, url, body) =>
  sendKeepalive(method, url, body).then(response =>
    response
      .json()
      .catch(() => ({}))
      .then(data =>
        response.ok
          ? { data }
          : Promise.reject(Object.assign(new Error(`HTTP ${response.status}`), { response: { status: response.status, data } }))
      )
  );

const locationParams = location => ({
  country: location.country,
  state: location.state || "",
  city_id: Number(location.city_id) || 0
});

/**
 * GET /api-v2/feed/suggested-communities — what the empty My Feed offers to follow (GROWTH-7135).
 * Not kept in the store: the grid asks again each time the empty feed mounts.
 *
 * @param {AbortSignal} [signal]
 * @returns {Promise<{communities: Object[], home_location: ?{country: string, state: string, city_id: number}}>}
 *   communities: {heritage_id, title, path, reason, reason_code, member_count}
 */
export const getSuggestedCommunities = signal => apiClient.get(SUGGESTED_COMMUNITIES_URL, { signal }).then(response => response.data.data);

export const actions = {
  setLoading(section, isLoading) {
    return { type: following.SET_LOADING, payload: { section, isLoading } };
  },

  /**
   * Loads the member's followed locations and community set.
   */
  getScope() {
    return dispatch => {
      dispatch(this.setLoading("scope", true));

      return apiClient
        .get(SCOPE_URL)
        .then(response => {
          dispatch({ type: following.GET_SCOPE, payload: response.data.data });
        })
        .catch(error => {
          dispatch({
            type: following.GET_SCOPE_FAIL,
            payload: failurePayload("scopeLoad", error, "We couldn't load what you follow.")
          });
        });
    };
  },

  /**
   * Follows a city, state or country with the member's current community set.
   *
   * @param {{country: string, state?: string, city_id?: number}} location
   * @param {function(): number} [now] clock for the change time
   * @returns {Promise<{created: boolean, location: Object, following_ids: number[]}>} rejects with the failure payload
   */
  addLocation(location, now = Date.now) {
    return dispatch => {
      dispatch(this.setLoading("locationAdd", true));

      return apiClient
        .post(LOCATIONS_URL, locationParams(location), { headers: { "Content-Type": "application/json" } })
        .then(response => {
          const result = response.data.data;
          dispatch({ type: following.ADD_LOCATION, payload: { location: result.location, changedAt: now() } });

          return result;
        })
        .catch(error => {
          const payload = failurePayload("locationAdd", error, "We couldn't follow that location.");
          dispatch({ type: following.ADD_LOCATION_FAIL, payload });

          return Promise.reject(payload);
        });
    };
  },

  /**
   * Unfollows one location.
   *
   * @param {{key: string, country: string, state?: string, city_id?: number}} location
   * @param {function(): number} [now] clock for the change time
   * @returns {Promise<number>} rows removed; rejects with the failure payload
   */
  removeLocation(location, now = Date.now) {
    return dispatch => {
      dispatch(this.setLoading("locationRemove", true));

      return apiClient
        .delete(LOCATIONS_URL, { params: locationParams(location) })
        .then(response => {
          dispatch({ type: following.REMOVE_LOCATION, payload: { key: location.key, changedAt: now() } });

          return response.data.data.removed;
        })
        .catch(error => {
          const payload = failurePayload("locationRemove", error, "We couldn't unfollow that location.");
          dispatch({ type: following.REMOVE_LOCATION_FAIL, payload });

          return Promise.reject(payload);
        });
    };
  },

  /**
   * Applies one community set to every followed location.
   *
   * @param {{all: boolean, heritage_ids: number[]}} communities
   * @param {function(): number} [now] clock for the change time
   * @param {{keepalive?: boolean}} [options] keepalive: send with `sendKeepalive` so the save survives
   *   the page closing (GROWTH-7102)
   * @returns {Promise<Object>} the new scope; rejects with the failure payload
   */
  saveCommunities(communities, now = Date.now, { keepalive = false } = {}) {
    return dispatch => {
      dispatch(this.setLoading("communitiesSave", true));
      const body = { all: Boolean(communities.all), heritage_ids: communities.all ? [] : communities.heritage_ids };
      const request = keepalive
        ? keepaliveRequest("PUT", COMMUNITIES_URL, body)
        : apiClient.put(COMMUNITIES_URL, body, { headers: { "Content-Type": "application/json" } });

      return request
        .then(response => {
          const scope = response.data.data;
          dispatch({ type: following.SAVE_COMMUNITIES, payload: { scope, changedAt: now() } });

          return scope;
        })
        .catch(error => {
          const payload = failurePayload("communitiesSave", error, "We couldn't save your community preferences.");
          dispatch({ type: following.SAVE_COMMUNITIES_FAIL, payload });

          return Promise.reject(payload);
        });
    };
  },

  /**
   * Loads the language → communities map once.
   */
  getLanguages() {
    return (dispatch, getState) => {
      const { languages, preloaders } = getState().following;
      if (languages.length || preloaders.languages) {
        return Promise.resolve();
      }

      dispatch(this.setLoading("languages", true));

      return apiClient
        .get(LANGUAGES_URL, { params: { command: "languages" } })
        .then(response => {
          dispatch({ type: following.GET_LANGUAGES, payload: response.data.data });
        })
        .catch(error => {
          dispatch({
            type: following.GET_LANGUAGES_FAIL,
            payload: failurePayload("languagesLoad", error, "We couldn't load the language filter.")
          });
        });
    };
  },

  /**
   * Searches cities, states, countries and zip/postal codes.
   *
   * @param {string} query
   * @param {AbortSignal} [signal] aborts the request when a newer search starts
   */
  searchPlaces(query, signal) {
    return dispatch => {
      dispatch(this.setLoading("places", true));

      return apiClient
        .get(PLACES_URL, { params: { q: query }, signal })
        .then(response => {
          dispatch({ type: following.SEARCH_PLACES, payload: response.data.data });
        })
        .catch(error => {
          if (isCanceledRequest(error)) {
            return;
          }
          dispatch({
            type: following.SEARCH_PLACES_FAIL,
            payload: failurePayload("placesSearch", error, "We couldn't search places right now.")
          });
        });
    };
  },

  clearPlaces() {
    return { type: following.CLEAR_PLACES };
  },

  /**
   * One-tap Follow on a suggested community (GROWTH-7135): adds the community to what the member
   * follows. With no followed location, the member's home city (or state) is followed first,
   * because PUT /user-following/communities rejects a save until one location is followed; that
   * location is unfollowed again when the community save then fails.
   *
   * Reads the scope fresh rather than from the store, so a stale store can't drop a community the
   * member already follows from the saved set.
   *
   * @param {number} heritageId community to follow
   * @param {{country: string, state: string, city_id: number}|null} homeLocation from the suggestions response
   * @returns {Promise<Object>} the new scope; rejects with `{section, message, status}`
   */
  followSuggestedCommunity(heritageId, homeLocation) {
    return async dispatch => {
      let scope;
      try {
        scope = (await apiClient.get(SCOPE_URL)).data.data;
      } catch (error) {
        return Promise.reject(failurePayload("suggestionFollow", error, "We couldn't follow that community."));
      }

      const hasLocation = scope.locations.length > 0;
      if (hasLocation && scope.communities.all) {
        const payload = { section: "suggestionFollow", message: "You already follow all communities.", status: null };

        return Promise.reject(payload);
      }
      if (!hasLocation && !homeLocation) {
        const payload = { section: "suggestionFollow", message: "Choose a location to follow first.", status: null };

        return Promise.reject(payload);
      }

      let addedLocation = null;
      if (!hasLocation) {
        addedLocation = (await dispatch(this.addLocation(homeLocation))).location;
      }

      const current = hasLocation ? scope.communities.heritage_ids : [];
      const heritageIds = [...new Set([...current, heritageId])].sort((a, b) => a - b);
      try {
        return await dispatch(this.saveCommunities({ all: false, heritage_ids: heritageIds }));
      } catch (payload) {
        if (addedLocation) {
          // The save's own failure is what the member needs to see; if the unfollow fails too, the
          // location stays followed.
          await Promise.resolve()
            .then(() => dispatch(this.removeLocation(addedLocation)))
            .catch(() => {});
        }

        return Promise.reject(payload);
      }
    };
  }
};
